import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onDrag' | 'onDragStart' | 'onDragEnd' | 'onAnimationStart' | 'onAnimationEnd'
> {
  children: ReactNode;
  to?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  onAction?: () => void;
}

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-on-primary hover:bg-primary-hover [&_svg]:transition-transform [&_svg]:duration-250 hover:[&_svg]:-translate-y-1',
  secondary:
    'border border-border-strong bg-surface text-text-primary hover:border-text-primary',
  outline:
    'border border-border-strong bg-transparent text-text-primary hover:border-primary hover:text-primary',
  ghost: 'bg-transparent text-text-primary hover:bg-surface-muted'
};

const sizes: Record<ButtonSize, string> = {
  sm: 'min-h-10 px-4 text-sm',
  md: 'min-h-11 px-5 text-sm',
  lg: 'min-h-12 px-6 text-[0.95rem]'
};

export function Button({
  children,
  to,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  onAction,
  className = '',
  type = 'button',
  ...buttonProps
}: ButtonProps) {
  const classes = [
    'group inline-flex cursor-pointer items-center justify-center gap-2 rounded-[var(--radius-sm)] font-semibold transition-[background-color,border-color,color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-55',
    variants[variant],
    sizes[size],
    fullWidth ? 'w-full' : '',
    className
  ]
    .filter(Boolean)
    .join(' ');

  if (to) {
    return (
      <span className={fullWidth ? 'block w-full' : 'inline-flex'}>
        <Link className={classes} to={to} onClick={onAction}>
          {children}
        </Link>
      </span>
    );
  }

  return (
    <button
      className={classes}
      type={type}
      {...buttonProps}
      onClick={event => {
        onAction?.();
        buttonProps.onClick?.(event);
      }}
    >
      {children}
    </button>
  );
}
